import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, CandyOffIcon, ServerCrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SnippetForm } from "~/components/common/settings/snippets/snippet-form";
import { useSnippetQuery } from "~/graphql/graphql.gen";

export const Route = createFileRoute(
  "/_auth/_app_shell/_settings-navigation/settings/snippet/$snippetId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { snippetId } = Route.useParams();

  const { data, error, isLoading } = useSnippetQuery(
    {
      supportSnippetId: snippetId,
    },
    {
      select: (data) => data.supportSnippet,
    },
  );

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full flex-col items-center justify-center overflow-auto">
          <Empty className="min-h-[300px] w-full max-w-md">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Spinner className="size-4" />
              </EmptyMedia>
              <EmptyTitle>{t("snippet.loading.title")}</EmptyTitle>
              <EmptyDescription>
                {t("snippet.loading.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );
  }

  if (!data || (error as Error | undefined)?.message === "snippet_not_found")
    return (
      <>
        <title>{`${t("snippet.not_found.title")} - ${t("app_name")}`}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CandyOffIcon />
              </EmptyMedia>
              <EmptyTitle>{t("snippet.not_found.title")}</EmptyTitle>
              <EmptyDescription>
                {t("snippet.not_found.description")}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <div className="bg-background flex size-full overflow-auto">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <ServerCrashIcon />
              </EmptyMedia>
              <EmptyTitle>{t("snippet.error")}</EmptyTitle>
            </EmptyHeader>
          </Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{`${t("snippet.edit_x", { name: data.title })} - ${t("app_name")}`}</title>

      <div className="bg-background w-full overflow-auto">
        <div className="m-auto max-w-2xl p-8">
          <header className="mt-14">
            <Button
              variant="ghost"
              size="sm"
              className="-mx-3 mb-4"
              nativeButton={false}
              render={
                <Link to="/settings/snippets">
                  <ArrowLeftIcon />
                  <span>{t("settings.snippets.all_snippets")}</span>
                </Link>
              }
            />

            <h1 className="text-2xl">{t("settings.snippets.add_snippet")}</h1>
          </header>
          <Separator className="my-6" />
          <SnippetForm snippet={data} />
        </div>
      </div>
    </>
  );
}
